import path from 'path';

import { GRAPHITE_DIR } from '../../constants/cache';
import { BLOG_POST_WP_TYPE } from '../../contentTypes/blogPost';
import {
  fetchFromPublicWithLog,
  readFileWithLog,
  readPublicFileWithLog
} from '../../helpers/fs-helpers';
import { isProductionBuild } from '../../helpers/is-env';
import { logWithPrefix } from '../../helpers/log-helpers';
import { returnBlogTemplate } from '../../lib/_client';
import {
  getBlogHomePageConfig,
  getTopics,
  returnStaticProps
} from '../../lib/_helpers';
import { isLocalizedBlog } from '../../lib/_i18n';
import Post from '../../templates/blog/Single';

const logPrefix = 'pages/blog/[slug]';
const cachedGraphiteData = {};

const getWPData = ({ locale, slug }) => {
  if (!process.env.VERCEL || process.env.CI === '1') {
    return readFileWithLog({
      defaultValue: null,
      filePath: path.join('./cache', BLOG_POST_WP_TYPE, `${slug}__${locale}.json`),
      logPrefix,
      noSuccessLog: true,
    });
  }

  return null;
};

const getGraphiteLinks = async ({ locale, slug }) => {
  // Same blog post across INTL could have the same slug
  if (!cachedGraphiteData[locale]) {
    const opts = {
      filePath: path.join(GRAPHITE_DIR, `${locale}.json`),
      logPrefix,
      noSuccessLog: true,
    };

    if (!process.env.VERCEL || process.env.CI === '1') {
      // If on local, always attempt to fetch from local file first
      // If on Vercel, make sure we do this only on CI (build time so we can use FS to read file)
      cachedGraphiteData[locale] = readPublicFileWithLog(opts);
    } else {
      // Fetch from public folder
      cachedGraphiteData[locale] = await fetchFromPublicWithLog(opts);
    }
  }

  return cachedGraphiteData[locale][slug] ?? [];
};

export default function BlogPost(props) {
  return returnBlogTemplate(props, Post);
}

export async function getStaticPaths() {
  let generatedPaths = [];

  if (isProductionBuild()) {
    const json = readFileWithLog({
      defaultValue: [],
      filePath: path.join('./cache', BLOG_POST_WP_TYPE, '_paths.json'),
      logPrefix,
      noSuccessLog: true,
    });

    const paths = json.map(({ locale, params: { slug } }) => {
      return {
        locale,
        params: { slug },
      };
    });

    generatedPaths = paths

      /**
       * Build the first 500 pages -- rest will be built on demand.
       *
       * In the future, to improve performance, we can split out INTL pages into separate pipeline
       */
      .slice(0, 500);

    logWithPrefix({
      message: `Total build-time paths: ${paths.length}`,
      prefix: logPrefix,
    });
    logWithPrefix({
      message: `Generated build-time paths: ${generatedPaths.length}`,
      prefix: logPrefix,
    });
  }

  return {
    fallback: 'blocking',
    paths: generatedPaths,
  };
}

export async function getStaticProps(props) {
  if (!isLocalizedBlog(props.locale)) {
    return { notFound: true };
  }

  const wpData = getWPData({
    locale: props.locale,
    slug: props.params.slug,
  });

  const graphiteLinks = await getGraphiteLinks({
    locale: props.locale,
    slug: props.params.slug,
  });

  const topicsPromise = getTopics();

  const blogHomePageConfigPromise = getBlogHomePageConfig();
  const postPromise = returnStaticProps({
    ...props,
    graphiteLinks,
    type: BLOG_POST_WP_TYPE,
    wpData,
  });
  const [
    topics,
    main,
    blogHomePageConfig,
  ] = await Promise.all([
    topicsPromise,
    postPromise,
    blogHomePageConfigPromise,
  ]);

  if (main?.props?.construct) {
    const blogHomeMeta =
      blogHomePageConfig.i18n?.[props.locale]?.source.blogHomeIndex ||
      blogHomePageConfig.blogHomeIndex;

    const categories = topics.categories.nodes.map((cat) => ({
      ...cat,
      name: blogHomeMeta.topicsData[cat.slug] || cat.name,
    }));

    main.props.construct.meta.topics = categories || null;

    main.props.construct.wp.categories = main.props.construct.wp.categories.map((cat) => ({
      ...cat,
      name: blogHomeMeta.topicsData[cat.slug] || cat.name,
    }));
  }

  return main;
}
